/**
 * Shared goal progress calculation - matches SQL get_goal_progress_percentage logic.
 * Running: LOWER time is better (target in description, current in current_value)
 * Weight loss: LOWER is better. Weight gain: HIGHER is better
 * Strength/Endurance reps: HIGHER is better. Endurance time: LOWER is better
 */

export interface GoalForProgress {
  goal_type?: string | null;
  exercise_name?: string | null;
  target_value: number;
  target_unit?: string | null;
  current_value: number;
  starting_value?: number | null;
  description?: string | null;
}

export function getGoalProgressPercentage(goal: GoalForProgress): number {
  const type = (goal.goal_type || '').toLowerCase();
  const name = (goal.exercise_name || '').toLowerCase();
  const unit = (goal.target_unit || '').toLowerCase();

  // Running: target time in description, current time in current_value. LOWER is better.
  if (type === 'running') {
    const targetTime = parseFloat(goal.description || '0');
    const currentTime = goal.current_value;
    const startingTime = goal.starting_value ?? (targetTime * 1.5);

    if (!targetTime || currentTime == null) return 0;
    if (currentTime <= targetTime) return 100;
    if (currentTime >= startingTime) return 0;

    const totalImprovement = startingTime - targetTime;
    const actualImprovement = startingTime - currentTime;
    return Math.min(Math.max((actualImprovement / totalImprovement) * 100, 0), 99);
  }

  // Weight / body fat
  const isWeightGoal =
    type === 'weight' ||
    name.includes('body weight') ||
    name.includes('weight loss') ||
    name.includes('weight gain') ||
    name.includes('body fat') ||
    name === 'weight';

  if (isWeightGoal) {
    const current = goal.current_value;
    const target = goal.target_value;
    const starting = goal.starting_value ?? current;

    if (!target || current == null) return 0;
    const isLossGoal = starting > target;
    const isGainGoal = starting < target;

    if (isLossGoal) {
      if (current <= target) return 100;
      if (current >= starting) return 0;
      const totalToLose = starting - target;
      const actualLost = starting - current;
      return Math.min(Math.max((actualLost / totalToLose) * 100, 0), 99);
    }
    if (isGainGoal) {
      if (current >= target) return 100;
      if (current <= starting) return 0;
      const totalToGain = target - starting;
      const actualGained = current - starting;
      return Math.min(Math.max((actualGained / totalToGain) * 100, 0), 99);
    }
    return current === target ? 100 : 0;
  }

  // Endurance time-based: LOWER is better
  if (type === 'endurance' && (unit === 'minutes' || unit === 'min' || unit === 'seconds' || unit === 'sec')) {
    const targetTime = goal.target_value;
    const currentTime = goal.current_value;
    const startingTime = goal.starting_value ?? (targetTime * 1.5);

    if (!targetTime || currentTime == null) return 0;
    if (currentTime <= targetTime) return 100;
    if (currentTime >= startingTime) return 0;

    const totalImprovement = startingTime - targetTime;
    const actualImprovement = startingTime - currentTime;
    return Math.min(Math.max((actualImprovement / totalImprovement) * 100, 0), 99);
  }

  // Default: strength, endurance reps - HIGHER is better
  const current = goal.current_value;
  const target = goal.target_value;
  const starting = goal.starting_value ?? (current > 0 ? 0 : current);

  if (!target || current == null) return 0;
  if (current >= target) return 100;

  const effectiveStarting = starting === current && current > 0 ? 0 : starting;
  if (current <= effectiveStarting) return 0;

  const totalToGain = target - effectiveStarting;
  const actualGained = current - effectiveStarting;
  return totalToGain > 0 ? Math.min(Math.max((actualGained / totalToGain) * 100, 0), 99) : 0;
}
